import { Separator } from "@monbits/ui";
import { ArrowRight, Copy, ExternalLink, MessageSquare } from "lucide-react";
import type * as React from "react";
import SpinWithMask from "src/components/spin-with-mask/spin-with-mask";
import TimeLabel from "src/components/time-label/time-label";
import { CURRENCY_SYMBOL, type PaymentLink } from "src/types";
import copyToClipboard from "src/utils/copy-to-clipboard";

interface Props {
	data?: PaymentLink;
	openNotificationFunc: (title: string, description: string) => void;
}

const PaymentLinkDescCard: React.FC<Props> = ({
	data,
	openNotificationFunc,
}) => {
	const copyText = (text: string) => {
		copyToClipboard(text, openNotificationFunc);
	};

	if (!data) {
		return <SpinWithMask isLoading />;
	}

	return (
		<div className="space-y-8">
			{/* Hero: price + name (when present). Payment links don't have
			    a status — they're a reusable shareable thing. */}
			<div className="space-y-1.5">
				<p className="text-xs text-muted-foreground">
					{data.name ? data.name : "Payment link"}
				</p>
				<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
					<span className="text-3xl font-semibold leading-none tracking-tight tabular-nums font-mono">
						{CURRENCY_SYMBOL[data.currency]}
						{data.price}
					</span>
					<span className="text-lg font-medium text-muted-foreground">
						{data.currency}
					</span>
				</div>
			</div>

			<Separator />

			{/* Link URL — the actionable hero. Same "Share with" pattern
			    as the payment card. */}
			<div className="flex gap-2">
				<button
					type="button"
					aria-label="Copy payment link URL"
					className="group flex-1 rounded-lg border border-border bg-muted/60 p-3 text-left transition-colors hover:bg-muted"
					onClick={() => copyText(data.url)}
				>
					<div className="space-y-1.5">
						<p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
							Share with customers
						</p>
						<div className="flex items-start gap-3">
							<span className="flex-1 text-sm font-mono break-all leading-relaxed">
								{data.url}
							</span>
							<Copy className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
						</div>
					</div>
				</button>
				<a
					href={data.url}
					target="_blank"
					rel="noopener noreferrer"
					aria-label="Open payment link in a new tab"
					className="flex flex-shrink-0 items-center justify-center rounded-lg border border-border bg-muted/60 px-3 transition-colors hover:bg-muted"
				>
					<ExternalLink className="h-4 w-4 text-muted-foreground hover:text-primary" />
				</a>
			</div>

			{/* After-payment behavior — labeled card showing redirect or
			    success-message configuration. */}
			<div className="rounded-lg border border-border bg-muted/40 p-3">
				<p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
					After payment
				</p>
				<div className="flex items-start gap-3">
					<div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
						{data.successAction === "redirect" ? (
							<ArrowRight className="h-3.5 w-3.5 text-primary" />
						) : (
							<MessageSquare className="h-3.5 w-3.5 text-primary" />
						)}
					</div>
					<div className="min-w-0 flex-1 space-y-0.5">
						<p className="text-sm font-medium">
							{data.successAction === "redirect"
								? "Redirect to URL"
								: "Show success message"}
						</p>
						{data.successAction === "redirect" ? (
							<p className="break-all font-mono text-xs text-muted-foreground">
								{data.redirectUrl || "Not configured"}
							</p>
						) : (
							<p className="text-xs leading-relaxed text-muted-foreground">
								{data.successMessage || "Not configured"}
							</p>
						)}
					</div>
				</div>
			</div>

			<Separator />

			{/* Metadata — tighter group of reference info. */}
			<dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm">
				<dt className="text-muted-foreground">Link ID</dt>
				<dd className="min-w-0">
					<button
						type="button"
						aria-label="Copy link ID"
						className="group ml-auto flex items-center gap-2"
						onClick={() => copyText(data.id)}
					>
						<span className="truncate font-mono text-xs">{data.id}</span>
						<Copy className="h-3 w-3 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
					</button>
				</dd>

				<dt className="text-muted-foreground">Created</dt>
				<dd className="text-right">
					<TimeLabel time={data.createdAt} />
				</dd>
			</dl>

			{/* Description — labeled card matching the after-payment card. */}
			{data.description && (
				<div className="rounded-lg border border-border bg-muted/40 p-3">
					<p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
						Description
					</p>
					<p className="text-sm leading-relaxed">{data.description}</p>
				</div>
			)}
		</div>
	);
};

export default PaymentLinkDescCard;
